// RX-AD (최종설계안_RX_20261007 §7) — 광고 슬롯 대장 · 크리에이티브 판정. 순수 함수만(RULES#143).
//
// ⛔ 판매·과금·단가는 PAY 트랙. 여기는 «자리 · 컴포넌트 · 계측» 까지다.
// ⛔ 가동 기본값 = 하우스만 ON. direct·adsense 는 DB 행이 있어도 ENABLED_TYPES 에 없으면 쓰지 않는다.
// ⚠️ 테이블(ad_creatives)이 없어도(마이그레이션 후행) 하우스 폴백으로 «빈 칸 0» 이 되도록 설계한다.

import { kstYmd } from '@/lib/cl-core/kst';
import { KAKAO_TALK_URL } from '@/lib/talk-banner';
import { CAFE_BASE_URL } from '@/lib/cafe/contract';

export const AD_SLOT_IDS = [
  'AD-CAL-PIN', 'AD-HOME-1', 'AD-HUB-1', 'AD-DETAIL-1', 'AD-BLOG-1', 'AD-STOCK-1', 'AD-SIDE-1',
] as const;
export type AdSlotId = (typeof AD_SLOT_IDS)[number];

export type CreativeType = 'house' | 'direct' | 'adsense';

export interface AdCreative {
  id: string;
  slotId: AdSlotId;
  type: CreativeType;
  title: string;
  sub: string;
  cta: string;
  targetUrl: string;
  /** 광고주 표기(스폰서 행). 하우스는 null. */
  advertiser: string | null;
  imageUrl?: string | null;
  /** VX-2 X8 — 이미지 카드 소재(하우스 「이번 주 분양 예정」)의 alt · 고지. 셋(imageUrl 포함)이 «다» 있을 때만 KdAdSlot 이 그림을 그린다. DB 소재에는 없다. */
  imageAlt?: string | null;
  imageCaption?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  /** 지역 셀렉 코드 배열(§4-5 단일 소스). 비면 전국. */
  regionCodes?: string[] | null;
  priority?: number;
}

export interface AdSlotSpec {
  id: AdSlotId;
  /** 예약 높이(px) — 렌더 전·후 불변(CLS 0). */
  reserveMobile: number;
  reserveDesktop: number;
  /** 데스크톱 ≥1024 에서만 렌더. */
  desktopOnly?: boolean;
  /** 콘텐츠형 스폰서 행(일정 배너 첫 칸) — 별도 렌더. */
  sponsorRow?: boolean;
}

export const AD_SLOT_SPECS: Record<AdSlotId, AdSlotSpec> = {
  'AD-CAL-PIN': { id: 'AD-CAL-PIN', reserveMobile: 52, reserveDesktop: 52, sponsorRow: true },
  'AD-HOME-1': { id: 'AD-HOME-1', reserveMobile: 120, reserveDesktop: 90 },
  'AD-HUB-1': { id: 'AD-HUB-1', reserveMobile: 76, reserveDesktop: 76 },
  'AD-DETAIL-1': { id: 'AD-DETAIL-1', reserveMobile: 120, reserveDesktop: 120 },
  'AD-BLOG-1': { id: 'AD-BLOG-1', reserveMobile: 120, reserveDesktop: 120 },
  'AD-STOCK-1': { id: 'AD-STOCK-1', reserveMobile: 120, reserveDesktop: 90 },
  'AD-SIDE-1': { id: 'AD-SIDE-1', reserveMobile: 250, reserveDesktop: 250, desktopOnly: true },
};

/** Q1 — 하우스만 ON. direct·adsense 는 PAY 트랙 개시 전까지 OFF. */
export const ENABLED_TYPES: readonly CreativeType[] = ['house'];

// 트라비스 하늘채(Node 본인 현장) — Q4 하우스 기본. slug 는 실데이터(apt_sites) 확인값.
export const TRAVIS_SLUG = '엄궁역-트라비스-하늘채';
export const CAFE_URL = CAFE_BASE_URL; // hc3 H6 — cafe/contract 단일 출처(값 불변)

const house = (slotId: AdSlotId, id: string, title: string, sub: string, cta: string, targetUrl: string): AdCreative =>
  ({ id, slotId, type: 'house', title, sub, cta, targetUrl, advertiser: null, priority: 0 });

/** 슬롯별 하우스 폴백 — 부정공 카톡방 / 리드폼(상세) / 네이버 카페 / 트라비스. 빈 칸 0. */
export const HOUSE_CREATIVES: Record<AdSlotId, AdCreative> = {
  'AD-CAL-PIN': { ...house('AD-CAL-PIN', 'house-cal-travis', '엄궁역 트라비스 하늘채', '선착순 동·호 지정 · 관심고객 등록', '상세 보기', `/apt/${encodeURIComponent(TRAVIS_SLUG)}`), advertiser: '스폰서' },
  'AD-HOME-1': house('AD-HOME-1', 'house-home-talk', '부정공 오픈카톡', '오늘의 분양 브리핑, 매일 아침 무료', '입장하기', KAKAO_TALK_URL),
  'AD-HUB-1': house('AD-HUB-1', 'house-hub-travis', '엄궁역 트라비스 하늘채', '선착순 동·호 지정 · 관심고객 등록', '상세 보기', `/apt/${encodeURIComponent(TRAVIS_SLUG)}`),
  'AD-DETAIL-1': house('AD-DETAIL-1', 'house-detail-cafe', '부정공 네이버 카페', '분양권 분석 자료실 · 무료 열람', '바로가기', CAFE_URL),
  'AD-BLOG-1': house('AD-BLOG-1', 'house-blog-talk', '부정공 오픈카톡', '현장별 소식, 묻고 답하기', '입장하기', KAKAO_TALK_URL),
  'AD-STOCK-1': house('AD-STOCK-1', 'house-stock-cafe', '부정공 네이버 카페', '분양·재개발 분석 자료실', '바로가기', CAFE_URL),
  'AD-SIDE-1': house('AD-SIDE-1', 'house-side-talk', '부정공 오픈카톡', '오늘의 분양 브리핑 받기', '입장하기', KAKAO_TALK_URL),
};

export function isAdSlotId(v: unknown): v is AdSlotId {
  return typeof v === 'string' && (AD_SLOT_IDS as readonly string[]).includes(v);
}

const today10 = (now: Date) => kstYmd(now.getTime());

/**
 * 슬롯에 실을 크리에이티브 1건. 후보(DB)에서 «가동 유형 · 기간 · 지역» 을 통과한 최고 priority,
 * 없으면 하우스. 항상 값을 돌려준다 — 빈 칸 0.
 */
export function pickCreative(
  slotId: AdSlotId,
  candidates: readonly AdCreative[] | null | undefined,
  opts: { regionCodes?: readonly string[] | null; now?: Date; enabled?: readonly CreativeType[] } = {},
): AdCreative {
  const now = opts.now ?? new Date();
  const t = today10(now);
  const enabled = opts.enabled ?? ENABLED_TYPES;
  const sel = new Set(opts.regionCodes ?? []);
  const ok = (candidates ?? []).filter((c) => {
    if (c.slotId !== slotId || !enabled.includes(c.type)) return false;
    if (c.startsAt && c.startsAt.slice(0, 10) > t) return false;
    if (c.endsAt && c.endsAt.slice(0, 10) < t) return false;
    if (!c.targetUrl || !c.title) return false;
    const rc = c.regionCodes ?? [];
    if (rc.length && !(sel.size && rc.some((x) => sel.has(x)))) return false;
    return true;
  });
  ok.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  return ok[0] ?? HOUSE_CREATIVES[slotId];
}

/** 외부 링크면 새 창·nofollow sponsored, 내부면 그대로. */
export function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url) && !/^https?:\/\/(www\.)?kadeora\.app/i.test(url);
}
